import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    RadioGroupProps,
    Typography,
    RadioGroup as MUIRadioGroup,
    FormControlLabel,
    Radio,
    FormHelperText,
    TextField,
    Backdrop,
    CircularProgress,
} from '@mui/material';
import {
    StepWrapper,
    StepperControls,
    StyledCardSection,
    YesNoSelector,
    InfoText,
    DecimalInput,
} from '@fnol/library/src/components';
import {
    GenericObject,
    checkSerbianBankAccount,
    checkSerbianIBAN,
    common,
    doesValueHaveMaxTwoDecimalPlaces,
} from '@fnol/library/src/lib';
import {
    bankType,
    personType,
    question50MaxLength,
    riskTypeLife,
    transferTypeValues,
    translations,
} from '../../lib/consts';
import { useValidateIndividual } from '../../lib/customHooks';
import Individual, { IndividualCleanup } from '../Individual/Individual';
import PaymentType, { PaymentCleanup } from '../PaymentType/PaymentType';
import { useApi } from '@fnol/library/src/hooks';
import { getOfferValidation } from '../../rpc/actions';

type PolicyUserProps = {
    navigator: GenericObject[];
};

const PaymentDetailScreen = ({ navigator }: PolicyUserProps) => {
    const [currentStep, { goBack, goForward }] = navigator;
    const { values, errors, touched, setErrors, setTouched, setFieldValue, submitForm } =
        useFormikContext<GenericObject>();

    const [checkingOffer1ForId, setCheckingOffer1ForId] = useState<string>('null');
    const [checkingOffer2ForId, setCheckingOffer2ForId] = useState<string>('null');
    const [checkedOffer1ForId, setCheckedOffer1ForId] = useState<boolean>(true);
    const [checkedOffer2ForId, setCheckedOffer2ForId] = useState<boolean>(true);

    const editIds = [1, 2, 3, 4, 5];
    const users: any = [];
    const createData = (name: string, payment: string, editId?: number) => {
        return { name, payment, editId };
    };

    if (!!values.userName) {
        if (
            values.risks.includes(riskTypeLife.purchase) ||
            (!values.risks.includes(riskTypeLife.purchase) && values.isSubmitterUser === 'false')
        ) {
            values.risks.includes(riskTypeLife.purchase) && values.userTypePurchase === 2
                ? users.push(createData(`${values.userBusinessName}`, `paymentToUser`))
                : users.push(createData(`${values.userName} ${values.userLastName}`, `paymentToUser`));
        }
    }
    if (values.isSubmitterUser === 'true' && !values.risks.includes(riskTypeLife.outlive)) {
        users.push(createData(`${values.submitterName} ${values.submitterLastName}`, `paymentToUser`));
    }

    editIds.map((editId) => {
        if (values[`user${editId}Name`]) {
            users.push(
                createData(
                    `${values[`user${editId}Name`]} ${values[`user${editId}LastName`]}`,
                    `paymentToUser${editId}`,
                    editId,
                ),
            );
        }
    });

    const saveOffer1Validation = (res: GenericObject) => {
        setFieldValue(`offer1Validation${checkingOffer1ForId}`, res.data.valid);
        setFieldValue(
            `offer1Validation${checkingOffer1ForId}Message`,
            res.data.valid === false ? res.data.poruka : null,
        );
        setCheckingOffer1ForId('null');
        setCheckedOffer1ForId(true);
    };

    const saveOffer2Validation = (res: GenericObject) => {
        setFieldValue(`offer2Validation${checkingOffer2ForId}`, res.data.valid);
        setFieldValue(
            `offer2Validation${checkingOffer2ForId}Message`,
            res.data.valid === false ? res.data.poruka : null,
        );
        setCheckingOffer2ForId('null');
        setCheckedOffer2ForId(true);
    };

    useEffect(() => {
        if (checkingOffer1ForId !== 'null') {
            fetchOffer1Validation();
        }
    }, [checkingOffer1ForId]);

    useEffect(() => {
        if (checkingOffer2ForId !== 'null') {
            fetchOffer2Validation();
        }
    }, [checkingOffer2ForId]);

    const { response: offer1Validation, fetchMethod: fetchOffer1Validation } = useApi(
        () =>
            getOfferValidation({
                brPonude: values[`transferOffer1Number${checkingOffer1ForId}`],
                brPolise: values[`activePolicyNumber`],
            }),
        saveOffer1Validation,
    );

    const { response: offer2Validation, fetchMethod: fetchOffer2Validation } = useApi(
        () =>
            getOfferValidation({
                brPonude: values[`transferOffer2Number${checkingOffer2ForId}`],
                brPolise: values[`activePolicyNumber`],
            }),
        saveOffer2Validation,
    );

    const confirmOfferNumber = (id: number, userId: number) => {
        const checkId = userId ? JSON.stringify(userId) : '';
        setFieldValue(`offer${id}Validation${checkId}`, true);
        setFieldValue(`offer${id}Validation${checkId}Message`, null);

        if (values[`transferOffer${id}Number${userId}`] && values[`transferOffer${id}Number${userId}`] != '') {
            if (id === 1) {
                setCheckingOffer1ForId(checkId);
            } else {
                setCheckingOffer2ForId(checkId);
            }

            if (checkingOffer1ForId !== 'null') {
                fetchOffer1Validation();
            }

            if (checkingOffer2ForId !== 'null') {
                fetchOffer2Validation();
            }
        } else {
            if (id === 1) {
                setCheckedOffer1ForId(true);
            } else {
                setCheckedOffer2ForId(true);
            }
        }
    };

    useEffect(() => {
        editIds.map((editId) => {
            if (!values[`user${editId}Name`]) {
                setFieldValue(`paymentToUser${editId}`, '');

                if (values.risks.includes(riskTypeLife.outlive)) {
                    setFieldValue(`outliveFullPayment${editId}`, '');
                }
            }
        });
    }, [users.length]);

    const personIds = users.map((user: GenericObject) => user.editId);
    const userValidation = useValidateIndividual(personType.user, personIds);
    const userValidationNoId = useValidateIndividual(personType.user);
    const thirdPersonValidation = useValidateIndividual(personType.thirdPerson, personIds);
    const thirdPersonValidationNoId = useValidateIndividual(personType.thirdPerson);

    useEffect(() => {
        if (values.submittedSuccessfully) {
            goForward();
        }
    }, [values.submittedSuccessfully]);

    const handleNext = (): void => {
        let valid = true;
        setTouched(
            {
                paymentToUser: true,
                paymentToUser1: true,
                paymentToUser2: true,
                paymentToUser3: true,
                paymentToUser4: true,
                paymentToUser5: true,
                outliveFullPayment: true,
                outliveFullPayment1: true,
                outliveFullPayment2: true,
                outliveFullPayment3: true,
                outliveFullPayment4: true,
                outliveFullPayment5: true,
                outliveTransfer: true,
                outliveTransfer1: true,
                outliveTransfer2: true,
                outliveTransfer3: true,
                outliveTransfer4: true,
                outliveTransfer5: true,
                transferOffer1Number: true,
                transferOffer1Number1: true,
                transferOffer1Number2: true,
                transferOffer1Number3: true,
                transferOffer1Number4: true,
                transferOffer1Number5: true,
                transfer1Amount: true,
                transfer1Amount1: true,
                transfer1Amount2: true,
                transfer1Amount3: true,
                transfer1Amount4: true,
                transfer1Amount5: true,
                transferOffer2Number: true,
                transferOffer2Number1: true,
                transferOffer2Number2: true,
                transferOffer2Number3: true,
                transferOffer2Number4: true,
                transferOffer2Number5: true,
                transfer2Amount: true,
                transfer2Amount1: true,
                transfer2Amount2: true,
                transfer2Amount3: true,
                transfer2Amount4: true,
                transfer2Amount5: true,
                ...userValidation.touched,
                ...userValidationNoId.touched,
                ...thirdPersonValidation.touched,
                ...thirdPersonValidationNoId.touched,
            },
            false,
        );

        setFieldValue('amountError', '');

        let existingErrors: GenericObject = {};

        const totalAmount = users
            .map((user: any) => {
                const userId = user.editId ? user.editId : '';
                return Number(values[`transfer1Amount${userId}`]) + Number(values[`transfer2Amount${userId}`]);
            })
            .reduce((total: number, current: number) => total + Number(current), 0);

        users.map((user: GenericObject) => {
            const userId = user.editId ? user.editId : '';
            if (
                values[`paymentToUser${userId}`] === '' &&
                (values[`outliveFullPayment${userId}`] === 'true' ||
                    values[`transferType${userId}`] === transferTypeValues.partial)
            ) {
                existingErrors[`paymentToUser${userId}`] = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (
                values[`paymentToUser${userId}`] === 'true' &&
                (values.inputPersonOpen == false || !values.inputPersonOpen)
            ) {
                if (!values[`user${userId}Bank`]) {
                    existingErrors[`user${userId}Bank`] = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (!values[`user${userId}BankName`]) {
                    existingErrors[`user${userId}BankName`] = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (!values[`user${userId}AccountNumber`]) {
                    existingErrors[`user${userId}AccountNumber`] = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                } else if (values[`user${userId}Bank`] === bankType.domestic) {
                    if (!checkSerbianBankAccount(values[`user${userId}AccountNumber`])) {
                        existingErrors[`user${userId}AccountNumber`] =
                            translations.validationMessages.ACCOUNT_NUMBER_INVALID_FORMAT;
                        valid = false;
                    }
                } else if (values[`user${userId}Bank`] === bankType.iban) {
                    if (!checkSerbianIBAN(values[`user${userId}AccountNumber`])) {
                        existingErrors[`user${userId}AccountNumber`] =
                            translations.validationMessages.ACCOUNT_NUMBER_INVALID_FORMAT_IBAN;
                        valid = false;
                    }
                }
            }

            if (
                ((values[`outliveFullPayment${userId}`] === 'true' ||
                    (values[`outliveFullPayment${userId}`] === 'false' &&
                        values[`outliveTransfer${userId}`] === 'false' &&
                        (!!values.risks.includes(riskTypeLife.outlive) ||
                            !!values.risks.includes(riskTypeLife.purchase)))) &&
                    !values[user.payment]) ||
                (!values.risks.includes(riskTypeLife.outlive) &&
                    !values.risks.includes(riskTypeLife.purchase) &&
                    !values[user.payment])
            ) {
                existingErrors[user.payment] = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!!values.risks.includes(riskTypeLife.outlive) || !!values.risks.includes(riskTypeLife.purchase)) {
                if (!values[`outliveFullPayment${userId}`]) {
                    existingErrors[`outliveFullPayment${userId}`] = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (values[`outliveFullPayment${userId}`] === 'false' && !values[`outliveTransfer${userId}`]) {
                    existingErrors[`outliveTransfer${userId}`] = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (values[`outliveTransfer${userId}`] === 'true' && !values[`transferType${userId}`]) {
                    existingErrors[`transferType${userId}`] = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (
                    values[`outliveTransfer${userId}`] === 'true' &&
                    !!values[`transfer1Amount${userId}`] &&
                    users.length >= 1 &&
                    values.paymentAmount &&
                    totalAmount >= values.paymentAmount
                ) {
                    setFieldValue('amountError', translations.validationMessages.OUTLIVE_AMOUNT_SUM_VALIDATION);
                    valid = false;
                }

                if (!!values[`transferType${userId}`] && !values[`transferOffer1Number${userId}`]) {
                    existingErrors[`transferOffer1Number${userId}`] = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (
                    (values[`transferType${userId}`] === transferTypeValues.partial ||
                        !!values[`transferOffer2Number${userId}`]) &&
                    !values[`transfer1Amount${userId}`]
                ) {
                    existingErrors[`transfer1Amount${userId}`] = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (
                    values[`transferType${userId}`] === transferTypeValues.full &&
                    !!values[`transfer1Amount${userId}`] &&
                    !!values[`transfer1Amount${userId}`] < values.paymentAmount &&
                    !values[`transferOffer2Number${userId}`]
                ) {
                    existingErrors[`transferOffer2Number${userId}`] = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (
                    values[`transferType${userId}`] === transferTypeValues.partial &&
                    !!values[`transferOffer2Number${userId}`] &&
                    !values[`transfer2Amount${userId}`]
                ) {
                    existingErrors[`transfer2Amount${userId}`] = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (
                    !!values[`transfer1Amount${userId}`] &&
                    values.paymentAmount &&
                    values[`transfer1Amount${userId}`] >= values.paymentAmount
                ) {
                    existingErrors[`transfer1Amount${userId}`] =
                        translations.validationMessages.OUTLIVE_AMOUNT_VALIDATION;
                    valid = false;
                }

                if (
                    !!values[`transfer2Amount${userId}`] &&
                    values.paymentAmount &&
                    values[`transfer2Amount${userId}`] >= values.paymentAmount
                ) {
                    existingErrors[`transfer2Amount${userId}`] =
                        translations.validationMessages.OUTLIVE_AMOUNT_VALIDATION;
                    valid = false;
                }

                if (
                    !!values[`transferOffer1Number${userId}`] &&
                    values[`offer1Validation${userId}Message`] &&
                    values[`offer1Validation${userId}Message`] !== ''
                ) {
                    valid = false;
                }

                if (
                    !!values[`transferOffer2Number${userId}`] &&
                    values[`offer2Validation${userId}Message`] &&
                    values[`offer2Validation${userId}Message`] !== ''
                ) {
                    valid = false;
                }
            }

            if (
                (values[`outliveFullPayment${userId}`] === 'true' ||
                    (values[`outliveFullPayment${userId}`] === 'false' &&
                        values[`outliveTransfer${userId}`] === 'false')) &&
                values[user.payment] === 'true'
            ) {
                if (!userValidation.isValid) valid = false;
            }

            if (
                (values[`outliveFullPayment${userId}`] === 'true' ||
                    (values[`outliveFullPayment${userId}`] === 'false' &&
                        values[`outliveTransfer${userId}`] === 'false')) &&
                values[user.payment] === 'false'
            ) {
                if (!thirdPersonValidation.isValid) valid = false;
            }
        });

        if (
            (values[`outliveFullPayment`] === 'true' ||
                (values[`outliveFullPayment`] === 'false' && values[`outliveTransfer`] === 'false')) &&
            values.isSubmitterUser === 'true' &&
            values.paymentToUser === 'true'
        ) {
            if (!userValidationNoId.isValid) valid = false;
        }

        if (
            !values.risks.includes(riskTypeLife.outlive) &&
            !values.risks.includes(riskTypeLife.purchase) &&
            values.isSubmitterUser === 'true' &&
            values.paymentToUser === 'false'
        ) {
            if (!thirdPersonValidationNoId.isValid) valid = false;
        }

        existingErrors = {
            ...existingErrors,
            ...userValidation.errors,
            ...thirdPersonValidation.errors,
        };

        setTimeout(() => {
            setErrors(existingErrors);
        });

        if (valid) {
            submitForm();
        }
    };

    const handleGoBack = (): void => {
        goBack();
    };

    const RadioGroup = (params: RadioGroupProps) => {
        return (
            <>
                <MUIRadioGroup {...params} sx={{ flexDirection: 'row' }}>
                    <FormControlLabel
                        value={transferTypeValues.partial}
                        control={<Radio />}
                        label={translations.OUTLIVE_TRANSFER_PARTIAL}
                    />
                    <FormControlLabel
                        value={transferTypeValues.full}
                        control={<Radio />}
                        label={translations.OUTLIVE_TRANSFER_FULL}
                    />
                </MUIRadioGroup>
            </>
        );
    };

    const handleChangePaymentToUser = (id: number) => {
        users.map((user: GenericObject) => {
            if (!!id) {
                IndividualCleanup(personType.thirdPerson, setFieldValue, id == user.editId ? user.editId : 111);
                PaymentCleanup(personType.thirdPerson, setFieldValue, id == user.editId ? user.editId : 111);
                PaymentCleanup(personType.user, setFieldValue, id == user.editId ? user.editId : 111);
            } else {
                IndividualCleanup(personType.thirdPerson, setFieldValue);
                PaymentCleanup(personType.thirdPerson, setFieldValue);
                PaymentCleanup(personType.user, setFieldValue);
            }
        });
    };

    const handleChangeOutlivePayment = (id: number) => {
        users.map((user: GenericObject) => {
            if (!!id && user.editId === id) {
                setFieldValue(`paymentToUser${user.editId}`, '');
                setFieldValue(`outliveTransfer${user.editId}`, '');
                setFieldValue(`transferType${user.editId}`, '');
                setFieldValue(`offer1Validation${user.editId}`, true);
                setFieldValue(`offer1Validation${user.editId}Message`, null);
                setFieldValue(`offer2Validation${user.editId}`, true);
                setFieldValue(`offer2Validation${user.editId}Message`, null);
            } else {
                setFieldValue(`paymentToUser`, '');
                setFieldValue(`outliveTransfer`, '');
                setFieldValue(`transferType`, '');
                setFieldValue(`offer1Validation`, true);
                setFieldValue(`offer1ValidationMessage`, null);
                setFieldValue(`offer2Validation`, true);
                setFieldValue(`offer2ValidationMessage`, null);
            }
            if (!!id) {
                IndividualCleanup(personType.thirdPerson, setFieldValue, id == user.editId ? user.editId : 111);
                PaymentCleanup(personType.thirdPerson, setFieldValue, id == user.editId ? user.editId : 111);
                PaymentCleanup(personType.user, setFieldValue, id == user.editId ? user.editId : 111);
            } else {
                IndividualCleanup(personType.thirdPerson, setFieldValue);
                PaymentCleanup(personType.thirdPerson, setFieldValue);
                PaymentCleanup(personType.user, setFieldValue);
            }
        });
    };

    const handleChangeOutliveTransfer = (id: number) => {
        users.map((user: GenericObject) => {
            if (!!id && user.editId === id) {
                setFieldValue(`transferType${user.editId}`, '');
                setFieldValue(`paymentToUser${user.editId}`, '');
                setFieldValue(`user${user.editId}AccountNumber`, '');
                setFieldValue(`user${user.editId}Bank`, '');
                setFieldValue(`user${user.editId}BankName`, '');
                setFieldValue(`thirdPerson${user.editId}AccountNumber`, '');
                setFieldValue(`thirdPerson${user.editId}Bank`, '');
                setFieldValue(`thirdPerson${user.editId}BankName`, '');
                setFieldValue(`offer1Validation${user.editId}`, true);
                setFieldValue(`offer1Validation${user.editId}Message`, null);
                setFieldValue(`offer2Validation${user.editId}`, true);
                setFieldValue(`offer2Validation${user.editId}Message`, null);
            } else {
                setFieldValue(`transferType`, '');
                setFieldValue(`paymentToUser`, '');
                setFieldValue(`userAccountNumber`, '');
                setFieldValue(`userBank`, '');
                setFieldValue(`userBankName`, '');
                setFieldValue(`thirdPersonAccountNumber`, '');
                setFieldValue(`thirdPersonBank`, '');
                setFieldValue(`thirdPersonBankName`, '');
                setFieldValue(`offer1Validation`, true);
                setFieldValue(`offer1ValidationMessage`, null);
                setFieldValue(`offer2Validation`, true);
                setFieldValue(`offer2ValidationMessage`, null);
            }
        });
    };

    const handleChangeOfferNumber = (e: React.ChangeEvent<HTMLSelectElement>, id: number, userId: number) => {
        if (id === 1) {
            setCheckedOffer1ForId(false);
        } else {
            setCheckedOffer2ForId(false);
        }
        users.map((user: GenericObject) => {
            if (!!userId && user.editId === userId) {
                setFieldValue(`transferOffer${id}Number${user.editId}`, e.target.value);
                setFieldValue(`offer${id}Validation${user.editId}Message`, '');
            } else if (values.risks.includes(riskTypeLife.purchase)) {
                setFieldValue(`transferOffer${id}Number`, e.target.value);
                setFieldValue(`offer${id}ValidationMessage`, '');
            }
        });
    };

    const handleChangeTransferType = (e: React.ChangeEvent<HTMLSelectElement>, id: number) => {
        users.map((user: GenericObject) => {
            if (!!id && user.editId === id) {
                setFieldValue(`transferType${user.editId}`, e.target.value);
                setFieldValue(`transferOffer1Number${user.editId}`, '');
                setFieldValue(`transfer1Amount${user.editId}`, '');
                setFieldValue(`transferOffer2Number${user.editId}`, '');
                setFieldValue(`transfer2Amount${user.editId}`, '');
                setFieldValue(`offer1Validation${user.editId}`, true);
                setFieldValue(`offer1Validation${user.editId}Message`, null);
                setFieldValue(`offer2Validation${user.editId}`, true);
                setFieldValue(`offer2Validation${user.editId}Message`, null);
                setFieldValue(`user${user.editId}AccountNumber`, '');
                setFieldValue(`user${user.editId}Bank`, '');
                setFieldValue(`user${user.editId}BankName`, '');
                setFieldValue(`paymentToUser${user.editId}`, '');
            } else if (values.risks.includes(riskTypeLife.purchase)) {
                setFieldValue(`transferType`, e.target.value);
                setFieldValue(`transferOffer1Number`, '');
                setFieldValue(`transfer1Amount`, '');
                setFieldValue(`transferOffer2Number`, '');
                setFieldValue(`transfer2Amount`, '');
                setFieldValue(`offer1Validation`, true);
                setFieldValue(`offer1ValidationMessage`, null);
                setFieldValue(`offer2Validation`, true);
                setFieldValue(`offer2ValidationMessage`, null);
                setFieldValue(`paymentToUser`, '');
            }
        });
    };

    const handleChangeTransferAmount = (e: React.ChangeEvent<HTMLSelectElement>, id: number, offerId: number): any => {
        if (doesValueHaveMaxTwoDecimalPlaces(e.target.value)) {
            setFieldValue(`transfer${offerId}Amount${id}`, e.target.value);
        }
    };

    return (
        <StepWrapper>
            {(offer1Validation.isLoading || offer2Validation.isLoading) && (
                <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={true}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
            {users?.map((user: GenericObject) => {
                const userId = user.editId ? user.editId : '';
                const outliveFullPayment = `outliveFullPayment${userId}`;
                const outliveTransfer = `outliveTransfer${userId}`;
                const transferType = `transferType${userId}`;
                const transferOffer1Number = `transferOffer1Number${userId}`;
                const transfer1Amount = `transfer1Amount${userId}`;
                const offer1NotValid = `offer1Validation${userId}Message`;
                const transferOffer2Number = `transferOffer2Number${userId}`;
                const transfer2Amount = `transfer2Amount${userId}`;
                const offer2NotValid = `offer2Validation${userId}Message`;

                return (
                    <StyledCardSection key={userId}>
                        {(values.risks.includes(riskTypeLife.outlive) ||
                            values.risks.includes(riskTypeLife.purchase)) && (
                            <>
                                <Typography variant="subtitle1">{translations.OUTLIVE_FULL_PAYMENT}</Typography>
                                <FormControl>
                                    <YesNoSelector
                                        name={outliveFullPayment}
                                        onChange={() => handleChangeOutlivePayment(userId)}
                                    />
                                </FormControl>

                                {values[outliveFullPayment] === 'false' && (
                                    <>
                                        <Typography variant="subtitle1">
                                            {values.risks.includes(riskTypeLife.outlive)
                                                ? translations.OUTLIVE_TRANSFER
                                                : translations.PURCHASE_TRANSFER}
                                        </Typography>
                                        <FormControl>
                                            <YesNoSelector
                                                name={outliveTransfer}
                                                onChange={() => handleChangeOutliveTransfer(userId)}
                                            />
                                        </FormControl>
                                    </>
                                )}
                            </>
                        )}

                        {values[outliveTransfer] === 'true' && (
                            <>
                                <FormControl>
                                    <Typography variant="subtitle1">{translations.OUTLIVE_TRANSFER_TYPE}</Typography>
                                    <Field
                                        type="boolean"
                                        name={transferType}
                                        as={RadioGroup}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                            handleChangeTransferType(e, userId)
                                        }
                                    />
                                    <FormHelperText error={true}>
                                        <>{errors[transferType]}</>
                                    </FormHelperText>
                                </FormControl>

                                {!!values[transferType] && (
                                    <FormControl>
                                        <Field
                                            name={transferOffer1Number}
                                            label={translations.OUTLIVE_OFFER}
                                            as={TextField}
                                            inputProps={{
                                                maxLength: question50MaxLength,
                                            }}
                                            onBlur={() => confirmOfferNumber(1, userId)}
                                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                                handleChangeOfferNumber(e, 1, userId)
                                            }
                                            error={!!(touched[transferOffer1Number] && errors[transferOffer1Number])}
                                            value={values[transferOffer1Number]}
                                            required
                                        />
                                        {touched[transferOffer1Number] && errors[transferOffer1Number] ? (
                                            <FormHelperText error={true}>
                                                <>{errors[transferOffer1Number]}</>
                                            </FormHelperText>
                                        ) : null}
                                    </FormControl>
                                )}

                                {!!values[transferOffer1Number] && !!values[offer1NotValid] ? (
                                    <FormHelperText error={true}>
                                        <>{values[offer1NotValid]}</>
                                    </FormHelperText>
                                ) : null}
                                {!!values[transferType] && (
                                    <FormControl>
                                        <Field
                                            name={transfer1Amount}
                                            label={translations.OUTLIVE_AMOUNT}
                                            as={DecimalInput}
                                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                                handleChangeTransferAmount(e, userId, 1)
                                            }
                                            onKeyDown={(e: any) => e.keyCode === 69 && e.preventDefault()}
                                            error={!!(touched[transfer1Amount] && errors[transfer1Amount])}
                                            value={values[transfer1Amount]}
                                            required={
                                                values[`transferType${userId}`] === transferTypeValues.partial ||
                                                !!values[`transferOffer2Number${userId}`]
                                            }
                                        />
                                        {touched[transfer1Amount] && errors[transfer1Amount] ? (
                                            <FormHelperText error={true}>
                                                <>{errors[transfer1Amount]}</>
                                            </FormHelperText>
                                        ) : null}
                                    </FormControl>
                                )}

                                {!!values[transferType] && (
                                    <FormControl>
                                        <Field
                                            name={transferOffer2Number}
                                            label={translations.OUTLIVE_OFFER}
                                            as={TextField}
                                            inputProps={{
                                                maxLength: question50MaxLength,
                                            }}
                                            onBlur={() => confirmOfferNumber(2, userId)}
                                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                                handleChangeOfferNumber(e, 2, userId)
                                            }
                                            error={!!(touched[transferOffer2Number] && errors[transferOffer2Number])}
                                            value={values[transferOffer2Number]}
                                        />
                                        {touched[transferOffer2Number] && errors[transferOffer2Number] ? (
                                            <FormHelperText error={true}>
                                                <>{errors[transferOffer2Number]}</>
                                            </FormHelperText>
                                        ) : null}
                                    </FormControl>
                                )}

                                {!!values[transferOffer2Number] && !!values[offer2NotValid] ? (
                                    <FormHelperText error={true}>
                                        <>{values[offer2NotValid]}</>
                                    </FormHelperText>
                                ) : null}

                                {!!values[transferType] && (
                                    <>
                                        {values[`transferType${userId}`] === transferTypeValues.partial ? (
                                            <FormControl>
                                                <Field
                                                    name={transfer2Amount}
                                                    label={translations.OUTLIVE_AMOUNT}
                                                    as={DecimalInput}
                                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                                        handleChangeTransferAmount(e, userId, 2)
                                                    }
                                                    onKeyDown={(e: any) => e.keyCode === 69 && e.preventDefault()}
                                                    error={!!(touched[transfer2Amount] && errors[transfer2Amount])}
                                                    value={values[transfer2Amount]}
                                                    required={
                                                        values[`transferType${userId}`] ===
                                                            transferTypeValues.partial &&
                                                        !!values[`transferOffer2Number${userId}`]
                                                    }
                                                />
                                                {touched[transfer2Amount] && errors[transfer2Amount] ? (
                                                    <FormHelperText error={true}>
                                                        <>{errors[transfer2Amount]}</>
                                                    </FormHelperText>
                                                ) : null}
                                            </FormControl>
                                        ) : (
                                            <Typography variant="subtitle1">
                                                {translations.OUTLIVE_TRANSFER_TYPE_AMOUNT_2}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {((values[outliveFullPayment] === 'true' && !values[outliveTransfer]) ||
                            values[outliveTransfer] === 'false' ||
                            (values[outliveTransfer] === 'true' &&
                                values[transferType] === transferTypeValues.partial) ||
                            (!values.risks.includes(riskTypeLife.outlive) &&
                                !values.risks.includes(riskTypeLife.purchase))) && (
                            <>
                                <Typography variant="subtitle1">
                                    {translations.PAYMENT_USER}
                                    <span style={{ fontWeight: 600 }}>{user.name}</span>?
                                </Typography>
                                <FormControl>
                                    <YesNoSelector
                                        name={user.payment}
                                        onChange={() => handleChangePaymentToUser(user.editId)}
                                    />
                                </FormControl>

                                {values[user.payment] === 'true' && (
                                    <PaymentType type={personType.user} id={user.editId}></PaymentType>
                                )}
                                {values[user.payment] === 'false' && (
                                    <>
                                        <InfoText label={translations.PAYMENT_REASON}></InfoText>
                                        <Individual type={personType.thirdPerson} id={user.editId}></Individual>
                                        <PaymentType type={personType.thirdPerson} id={user.editId}></PaymentType>
                                    </>
                                )}
                            </>
                        )}

                        <>
                            {values.amountError !== '' && (
                                <FormHelperText error={true}>
                                    <>{values.amountError}</>
                                </FormHelperText>
                            )}
                        </>
                    </StyledCardSection>
                );
            })}

            <StepperControls
                onNext={
                    checkedOffer1ForId &&
                    checkedOffer2ForId &&
                    (!values[`offerValidation${checkingOffer1ForId}Message`] ||
                        values[`offerValidation${checkingOffer1ForId}Message`] === '')
                        ? handleNext
                        : undefined
                }
                onPrevious={handleGoBack}
                onNextLabel={common.NEXT}
                onPreviousLabel={common.PREVIOUS}
                // disableNext={!checkedOffer1ForId || !checkedOffer2ForId}
            />
        </StepWrapper>
    );
};

export default PaymentDetailScreen;
